import * as React from 'react';
import { useEffect, useState } from 'react';
import IconButton from '@mui/joy/IconButton';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import { KeyboardArrowLeft, KeyboardArrowRight, Message, PhoneCallback } from "@mui/icons-material";
import { CircularProgress, Chip } from "@mui/joy";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useSearchParams } from "react-router-dom";
import CustomersModal from "../modals/CustomersModal";
import DebounceInput from "../../domain/DebounceInput";
import { useCall } from "../../lib/call-context";
import ApiService from "../../axios/Bookings-Axios";
import { normalizeUkPhone, ukPhoneVariants } from "../../lib/phone";
import ResponsiveTableWrapper from "../ui/ResponsiveTableWrapper";

interface Row {
    full_name: string;
    street_address: string;
    postcode: string;
    phone: string;
    house_type: string;
    id: string;
    email: string;
    expanded_row: RowExpanded;
}

interface RowExpanded {
    notes: string;
    id: string;
}

function Row(props: { row: Row; initialOpen?: boolean, onRowClick: (rowId: number) => void }) {
    const { row } = props;
    const [open, setOpen] = useState(props.initialOpen || false);
    const { makeCall } = useCall();

    const handleOpenEditCustomer = () => {
        props.onRowClick(Number(row.id));
    };

    const handleCallCustomer = () => {
        if (row.phone) {
            makeCall(row.phone, row.full_name);
        }
    };

    return (
        <React.Fragment>
            <tr>
                <td><IconButton aria-label="expand row" variant="plain" color="neutral" size="sm" onClick={() => setOpen(!open)}>{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></td>
                <td>{row.full_name}</td>
                <td>{row.phone}</td>
                <td>{row.email}</td>
                <td>{row.street_address}</td>
                <td>{row.postcode}</td>
                <td>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button sx={{ textWrap: 'nowrap' }} size="sm" color="primary" onClick={handleOpenEditCustomer}>Edit Customer</Button>
                        <IconButton variant="solid" sx={{ ml: 1, textWrap: 'nowrap' }} size="sm" color="primary" onClick={handleCallCustomer}><PhoneCallback /></IconButton>
                    </Box>
                </td>
            </tr>
            <tr>
                <td style={{ height: 0, padding: 0 }} colSpan={7}>
                    {open && (
                        <Sheet variant="soft" sx={{ p: 1, pl: 6, boxShadow: 'inset 0 3px 6px 0 rgba(0 0 0 / 0.08)' }}>
                            <Table borderAxis="bothBetween" aria-label="notes">
                                <thead><tr><th>Notes</th></tr></thead>
                                <tbody><tr><td style={{ whiteSpace: 'pre-wrap' }}>{row.expanded_row.notes || 'No notes'}</td></tr></tbody>
                            </Table>
                        </Sheet>
                    )}
                </td>
            </tr>
        </React.Fragment>
    );
}

const mapRawDataToRow = (data: any[]): Row[] => {
    return data.map(item => ({
        full_name: item.full_name,
        street_address: item.street_address,
        postcode: item.postcode,
        phone: item.phone,
        house_type: item.house_type?.name || '',
        id: item.id.toString(),
        email: item.email,
        expanded_row: {
            notes: item.notes,
            id: item.id,
        }
    }));
};

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number; }) {
    return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

export default function CustomersTable() {
    const [loadedData, setLoadingData] = useState(false);
    const [rows, setRows] = useState<Row[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageCount, setPageCount] = useState(0);
    const [search, setSearch] = useState('');
    const [urlSearchParam, setUrlSearchParam] = useSearchParams();
    const [openCustomerId, setOpenCustomerId] = useState(0);
    const [openCustomerModal, setOpenCustomerModal] = useState(false);
    const { makeCall, call, incomingCall } = useCall();
    const [activeCallRow, setActiveCallRow] = useState<Row | null>(null);

    useEffect(() => {
        let cancelled = false;
        const remoteNumber = incomingCall?.parameters.From || call?.parameters.To;
        const targetNorm = remoteNumber ? normalizeUkPhone(remoteNumber) : null;

        if (!remoteNumber || !targetNorm) {
            setActiveCallRow(null);
            return () => { cancelled = true; };
        }

        const terms = ukPhoneVariants(remoteNumber);

        (async () => {
            for (const term of terms) {
                try {
                    const resp = await ApiService.getCustomers({ search: term, page: 1 });
                    const results = resp?.results || [];
                    if (!results.length) continue;

                    const mapped = mapRawDataToRow(results);
                    const exact = mapped.filter((r) => normalizeUkPhone(r.phone) === targetNorm);
                    if (!exact.length) continue;

                    // Pick latest CUSTOMER (highest customer id) among exact phone matches
                    const best = exact.slice().sort((a, b) => Number(b.id) - Number(a.id))[0];

                    if (!cancelled) setActiveCallRow(best || null);
                    return;
                } catch {
                    // try next term
                }
            }
            if (!cancelled) setActiveCallRow(null);
        })();

        return () => { cancelled = true; };
    }, [call, incomingCall]);

    const handleCallback = () => {
        setOpenCustomerModal(false);
    };

    const handleOpenCustomerModal = (rowId: number) => {
        setOpenCustomerModal(true);
        setOpenCustomerId(rowId);
    };

    const getLabelDisplayedRowsTo = () => {
        return Math.min(pageCount, (page + 1) * rowsPerPage);
    };

    useEffect(() => {
        setLoadingData(false);
        const fetchData = async () => {
            try {
                const customersData = await ApiService.getCustomers({ page: page + 1, search });
                setRows(mapRawDataToRow(customersData.results || []));
                setPageCount(customersData.count || 0);
            } catch (error) {
                console.error("Failed to fetch customers:", error);
                setRows([]);
                setPageCount(0);
            } finally {
                setLoadingData(true);
            }
        };
        fetchData();
    }, [page, search]);

    const handleDebounce = (value: string) => {
        setPage(0);
        setSearch(value);
    };

    return (
        <>
            <Box sx={{ mb: 2 }}>
                <DebounceInput 
                    sx={{ width: { xs: '100%', sm: '250px' } }} 
                    startDecorator={<SearchRoundedIcon />}
                    placeholder="Search by any field..."
                    debounceTimeout={1000}
                    defaultValue={urlSearchParam.get("customer-search") as string}
                    handleDebounce={handleDebounce}
                />
            </Box>

            {/* Active Call UI removed per request */}

            <ResponsiveTableWrapper minWidth={700}>
                <Table hoverRow aria-label="collapsible table">
                    <thead>
                    <tr>
                        <th style={{ width: 40 }} aria-label="empty" />
                        <th>Full Name</th>
                        <th>Number</th>
                        <th>Email</th>
                        <th>Street Address</th>
                        <th>Postcode</th>
                        <th aria-label="last" />
                    </tr>
                    </thead>
                    <tbody>
                    {loadedData ? (
                        rows.map((row) => (
                            <Row key={row.id} row={row} initialOpen={search.length > 0} onRowClick={handleOpenCustomerModal} />
                        ))
                    ) : (
                        <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}><CircularProgress /></td></tr>
                    )}
                    </tbody>
                    <tfoot>
                    <tr>
                        <td colSpan={7}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                                <Typography textAlign="center" sx={{ minWidth: 80 }}>
                                    {labelDisplayedRows({
                                        from: pageCount === 0 ? 0 : page * rowsPerPage + 1,
                                        to: getLabelDisplayedRowsTo(),
                                        count: pageCount,
                                    })}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton size="sm" color="neutral" variant="outlined" disabled={page === 0} onClick={() => setPage(page - 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowLeft /></IconButton>
                                    <IconButton size="sm" color="neutral" variant="outlined" disabled={page >= Math.ceil(pageCount / rowsPerPage) - 1} onClick={() => setPage(page + 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowRight /></IconButton>
                                </Box>
                            </Box>
                        </td>
                    </tr>
                    </tfoot>
                </Table>
            </ResponsiveTableWrapper>
            <React.Fragment>
                <CustomersModal key={'customer-modal-' + openCustomerModal} openState={openCustomerModal}
                                customerId={openCustomerId} parentCallback={handleCallback} />
            </React.Fragment>

        </>
    );
}